import _get from 'lodash/get';
import React, { useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { Accordion, Form, Table, Button, Stack } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import Schema from '@ai4/form-viewer/dist/types/schema';
import { FormViewer, SchemaTypes, traverseSchema } from '@ai4/form-viewer';
import { useDataRequest } from '@ai4/data-request';
import { Role } from '../types';
import $schema from './role.schema.json';
import { PermissionsContext } from '../context';
let schema = $schema as SchemaTypes.Schema;

interface Props {
	id: string;
	onBack: () => void;
}

function RolesDetail(props: Props) {
	const { id, onBack } = props;
	const { canRoles } = useContext(PermissionsContext);
	const [role, setRole] = useState<Role>();
	const [featureMap, setFeatureMap] = useState<any>();
	const { useRestRequest } = useDataRequest();
	const [req] = useRestRequest({
		path: `api/{ver}/roles/${id}`,
		method: 'GET',
		jwt: true,
	});
	const [reqFeats] = useRestRequest({
		path: `api/{ver}/features/permissions`,
		method: 'GET',
		jwt: true,
	});
	const [reqSave] = useRestRequest({
		path: `api/{ver}/roles`,
		method: 'POST',
		jwt: true,
	});

	const load = async () => {
		const role = (await req()) as Role;
		setRole(role);
		const feats = await reqFeats();
		const map = (feats as any).reduce((acc: any, feat: any) => {
			if (feat.categories === null) return acc;
			if (!acc[feat.moduleName]) acc[feat.moduleName] = {};
			feat.categories.forEach(({ name: categoryName, permissions }: any) => {
				if (!acc[feat.moduleName][categoryName]) acc[feat.moduleName][categoryName] = [];
				acc[feat.moduleName][categoryName] = [
					...acc[feat.moduleName][categoryName],
					...permissions.map((perm: any) => ({
						...perm,
						code: perm.code,
						isPermissionEnabled: !!_get(perm, 'roles', []).find((r: Role) => r.id === role.id),
					})),
				];
			});
			return acc;
		}, {});
		setFeatureMap(map);
	};

	useEffect(() => {
		load();
	}, []);

	const submit = useCallback(async ({ values }) => {
		try {
			await reqSave({
				data: {
					id,
					...values,
				},
			});
			alert('Ruolo salvato correttamente');
		} catch (e) {
			alert('Errore nel salvataggio');
		}
	}, []);

	schema = useMemo(() => {
		return traverseSchema(schema, (n: SchemaTypes.SchemaNode) => {
			if (n.id && n.id === 'name')
				return {
					...n,
					props: {
						readonly: true,
					},
				};
			return n;
		});
	}, [schema]);

	const accordionClassName = isMobile ? 'accordion-lg' : undefined;

	if (!role || !featureMap) return null;

	const modules = Object.keys(featureMap);
	return (
		<div>
			<Stack direction='horizontal' gap={3}>
				<div>
					<div
						className='back'
						onClick={() => {
							if (onBack) onBack();
						}}
					>
						<Button variant='outline-secondary'>
							<i className='bi bi-chevron-left' />
						</Button>
					</div>
				</div>
				<div>
					<h1>{role.name}</h1>
				</div>
			</Stack>

			<FormViewer
				initialValues={role || {}}
				schema={$schema as Schema}
				onSubmit={submit}
				slots={{
					ButtonBar: canRoles && canRoles.edit ? undefined : () => <div />,
				}}
			/>

			<hr />
			<h4>Permessi</h4>

			<Accordion defaultActiveKey={modules[0]} className={accordionClassName}>
				{modules.map((module) => {
					return (
						<Accordion.Item key={module} className='ul-card__border-radius card border-0' eventKey={module}>
							<Accordion.Header>
								<h6 className='card-title mb-0 text-primary'>{module}</h6>
							</Accordion.Header>
							<Accordion.Body>
								<DetailModule role={role} module={featureMap[module]} />
							</Accordion.Body>
						</Accordion.Item>
					);
				})}
			</Accordion>
		</div>
	);
}

interface DetailModuleProps {
	role: Role;
	module: any;
}

function DetailModule(props: DetailModuleProps) {
	const { role, module } = props;

	const categories = Object.keys(module);
	return (
		<div>
			{categories.map((category) => {
				const permissions = module[category];

				return (
					<div key={category}>
						<h4>{category}</h4>
						<div>
							<DetailModulePermissions role={role} permissions={permissions} />
						</div>
					</div>
				);
			})}
		</div>
	);
}

interface DetailModulePermissionsProps {
	role: Role;
	permissions: any[];
}

function DetailModulePermissions(props: DetailModulePermissionsProps) {
	const { role, permissions } = props;
	const [busy, setBusy] = useState(false);
	const [enableMap, setEnableMap] = useState<Record<string, boolean>>(
		permissions.reduce(
			(acc, perm) => ({
				...acc,
				[perm.code]: perm.isPermissionEnabled,
			}),
			{}
		)
	);
	const { useRestRequest } = useDataRequest();

	const [req] = useRestRequest({
		path: `api/{ver}/roles/${role.id}/permissions/{perm_code}`,
		method: 'POST|DELETE',
		jwt: true,
	});

	return (
		<div>
			<Table striped bordered hover>
				<thead>
					<tr>
						<th>Funzione</th>
						<th>Stato</th>
					</tr>
				</thead>
				<tbody>
					{permissions.map((row, k) => {
						const checked = enableMap[row.code];
						const description = isMobile ? null : (
							<div>
								<span className='row-subtitle'>{row.description}</span>
							</div>
						);
						return (
							<tr key={k}>
								<td>
									<div>
										<span>{row.name}</span>
										{description}
									</div>
								</td>
								<td>
									<Form.Check
										type='checkbox'
										id={`enable_${row.id}`}
										label='Abilita'
										checked={checked}
										disabled={busy}
										onChange={async (val) => {
											setBusy(true);
											try {
												await req({
													path: `api/{ver}/roles/${role.id}/permissions/${row.code}`,
													method: checked ? 'DELETE' : 'POST',
												});
												setEnableMap({
													...enableMap,
													[row.code]: !checked,
												});
											} catch (e) {
												alert('Errore nel cambio stato');
											} finally {
												setBusy(false);
											}
										}}
									/>
								</td>
							</tr>
						);
					})}
				</tbody>
			</Table>
		</div>
	);
}

export default RolesDetail;
